import api from './authService'
import { crearErrorAplicacionApi } from './apiError'

export interface UsuarioAPI {
  id: number | string
  nombre_usuario: string
  correo: string
  rol: string
  rol_global?: 'superadmin' | 'admin' | 'usuario'
  rol_organizacion?: string | null
  tipo_organizacion?: string | null
  id_organizacion?: number | null
  activo: boolean
  empresa?: string
  membresia_activa?: boolean
  plan_membresia?: string
  fecha_expiracion_membresia?: string
  fecha_creacion?: string
  ultimo_ingreso?: string
  telefono?: string
  apellido?: string
  id_institucion?: number
}

export interface Usuario {
  id: string
  nombre: string
  apellido?: string
  email: string
  telefono?: string
  empresa?: string
  rol_global: 'superadmin' | 'admin' | 'usuario'
  rol_organizacion?: string
  tipo_organizacion?: string
  id_organizacion?: number
  rol: 'superadmin' | 'admin' | 'usuario' | 'moderador' | 'institucion_admin' | 'institucion_supervisor' | 'institucion_jefe' | 'institucion_usuario' | 'institucion_alumno' | 'institucion_maestro' | 'empresa_admin' | 'empresa_moderador' | 'empresa_supervisor' | 'empresa_contador' | 'empresa_usuario'
  estado: 'activo' | 'inactivo' | 'suspendido'
  fechaRegistro: string
  ultimoAcceso?: string
  avatar?: string
  verificado: boolean
  suscripcion?: 'basica' | 'premium' | 'empresarial' | 'institucional'
  id_institucion?: number
}

export interface FiltrosUsuarios {
  busqueda?: string
  rol?: string
  estado?: string
  suscripcion?: string
  fechaDesde?: string
  fechaHasta?: string
  limite?: number
  offset?: number
}

export interface EstadisticasUsuarios {
  total: number
  activos: number
  inactivos: number
  porRol: Record<string, number>
  porRolGlobal: Record<string, number>
  porTipoOrganizacion: Record<string, number>
  porRolOrganizacion: {
    institucion: Record<string, number>
    empresa: Record<string, number>
  }
  porSuscripcion: Record<string, number>
  registrosHoy: number
  registrosEsteMes: number
}

export interface UsuarioCrear {
  nombre_usuario: string
  correo: string
  password: string
  rol?: 'superadmin' | 'admin' | 'user' | 'institucion_admin' | 'institucion_supervisor' | 'institucion_jefe' | 'institucion_usuario' | 'institucion_alumno' | 'institucion_maestro' | 'empresa_admin' | 'empresa_moderador' | 'empresa_supervisor' | 'empresa_contador' | 'empresa_usuario'
  empresa?: string
  telefono?: string
  apellido?: string
  id_institucion?: number
  id_organizacion?: number
  rol_organizacion?: string
}

export interface UsuarioActualizar {
  nombre_usuario?: string
  correo?: string
  rol?: 'superadmin' | 'admin' | 'user' | 'institucion_admin' | 'institucion_usuario' | 'institucion_supervisor' | 'institucion_jefe' | 'institucion_alumno' | 'institucion_maestro' | 'empresa_admin' | 'empresa_moderador' | 'empresa_supervisor' | 'empresa_contador' | 'empresa_usuario'
  activo?: boolean
  empresa?: string
  telefono?: string
  apellido?: string
  id_institucion?: number
  id_organizacion?: number
  rol_organizacion?: string
  plan_membresia?: string
}

export const usuariosService = {
  derivarRolDesdeContexto(
    rolGlobal?: string | null,
    tipoOrganizacion?: string | null,
    rolOrganizacion?: string | null,
    rolLegacy?: string | null
  ): Usuario['rol'] {
    const rolDerivado = rolLegacy ?? (
      tipoOrganizacion && rolOrganizacion ? `${tipoOrganizacion}_${rolOrganizacion}` : rolGlobal ?? 'usuario'
    )

    if (rolDerivado === 'superadmin') return 'superadmin'
    if (rolDerivado === 'admin') return 'admin'
    if (rolDerivado === 'moderador') return 'moderador'
    if (rolDerivado === 'institucion_admin') return 'institucion_admin'
    if (rolDerivado === 'institucion_supervisor') return 'institucion_supervisor'
    if (rolDerivado === 'institucion_jefe') return 'institucion_jefe'
    if (rolDerivado === 'institucion_usuario') return 'institucion_usuario'
    if (rolDerivado === 'institucion_alumno') return 'institucion_alumno'
    if (rolDerivado === 'institucion_maestro') return 'institucion_maestro'
    if (rolDerivado === 'empresa_admin') return 'empresa_admin'
    if (rolDerivado === 'empresa_moderador') return 'empresa_moderador'
    if (rolDerivado === 'empresa_supervisor') return 'empresa_supervisor'
    if (rolDerivado === 'empresa_contador') return 'empresa_contador'
    if (rolDerivado === 'empresa_usuario') return 'empresa_usuario'
    return 'usuario'
  },

  formatearEtiquetaRol(usuario: Pick<Usuario, 'rol' | 'rol_global' | 'rol_organizacion' | 'tipo_organizacion'>): string {
    if (usuario.tipo_organizacion === 'institucion' && usuario.rol_organizacion) {
      const etiquetas: Record<string, string> = {
        admin: 'Institucion Admin',
        supervisor: 'Institucion Supervisor',
        jefe: 'Institucion Jefe',
        usuario: 'Institucion Usuario',
        alumno: 'Institucion Alumno',
        maestro: 'Institucion Maestro',
      }
      return etiquetas[usuario.rol_organizacion] || usuario.rol
    }
    if (usuario.tipo_organizacion === 'empresa' && usuario.rol_organizacion) {
      const etiquetas: Record<string, string> = {
        admin: 'Empresa Admin',
        moderador: 'Empresa Moderador',
        supervisor: 'Empresa Supervisor',
        contador: 'Empresa Contador',
        usuario: 'Empresa Usuario',
      }
      return etiquetas[usuario.rol_organizacion] || usuario.rol
    }
    const etiquetasGlobales: Record<string, string> = {
      superadmin: 'Superadmin',
      admin: 'Administrador',
      usuario: 'Usuario',
      moderador: 'Moderador',
    }
    return etiquetasGlobales[usuario.rol_global] || etiquetasGlobales[usuario.rol] || usuario.rol
  },

  mapearRolFormularioAPayload(usuario: Pick<Usuario, 'rol_global' | 'rol_organizacion' | 'tipo_organizacion' | 'id_institucion'>): UsuarioCrear['rol'] {
    if (usuario.tipo_organizacion === 'institucion' && usuario.rol_organizacion) {
      return `institucion_${usuario.rol_organizacion}` as UsuarioCrear['rol']
    }
    if (usuario.tipo_organizacion === 'empresa' && usuario.rol_organizacion) {
      return `empresa_${usuario.rol_organizacion}` as UsuarioCrear['rol']
    }
    if (usuario.rol_global === 'superadmin') return 'superadmin'
    if (usuario.rol_global === 'admin') return 'admin'
    return 'user'
  },

  /**
   * Normalizar usuario de la API al formato del frontend
   */
  normalizarUsuario(usuarioAPI: UsuarioAPI): Usuario {
    const rolGlobal = usuarioAPI.rol_global === 'superadmin'
      ? 'superadmin'
      : usuarioAPI.rol_global === 'admin'
        ? 'admin'
        : 'usuario'
    const rolOrganizacion = usuarioAPI.rol_organizacion ?? undefined
    const tipoOrganizacion = usuarioAPI.tipo_organizacion ?? undefined
    const rol = this.derivarRolDesdeContexto(rolGlobal, tipoOrganizacion, rolOrganizacion, usuarioAPI.rol)

    // Asegurar que ID sea string y no undefined, buscando también id_usuario por compatibilidad
    const rawId = usuarioAPI.id ?? (usuarioAPI as any).id_usuario;
    const id = rawId !== undefined && rawId !== null 
      ? String(rawId) 
      : `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    return {
      id,
      nombre: usuarioAPI.nombre_usuario,
      apellido: usuarioAPI.apellido || '',
      email: usuarioAPI.correo,
      telefono: usuarioAPI.telefono,
      empresa: usuarioAPI.empresa,
      rol_global: rolGlobal,
      rol_organizacion: rolOrganizacion ?? undefined,
      tipo_organizacion: tipoOrganizacion ?? undefined,
      id_organizacion: usuarioAPI.id_organizacion ?? undefined,
      rol,
      estado: usuarioAPI.activo ? 'activo' : 'inactivo',
      fechaRegistro: usuarioAPI.fecha_creacion || new Date().toISOString(),
      ultimoAcceso: usuarioAPI.ultimo_ingreso,
      verificado: true,
      suscripcion: usuarioAPI.plan_membresia === 'premium' ? 'premium' : 
                   usuarioAPI.plan_membresia === 'empresarial' ? 'empresarial' : 
                   usuarioAPI.plan_membresia === 'institucional' ? 'institucional' : 'basica',
      id_institucion: usuarioAPI.id_institucion
    }
  },

  /**
   * Obtener lista de usuarios
   */
  async obtenerUsuarios(filtros?: FiltrosUsuarios): Promise<Usuario[]> {
    try {
      const params = new URLSearchParams()
      if (filtros?.limite) params.append('limite', filtros.limite.toString())
      if (filtros?.offset) params.append('offset', filtros.offset.toString())
      if (filtros?.busqueda) params.append('busqueda', filtros.busqueda)
      if (filtros?.rol) params.append('rol', filtros.rol)
      if (filtros?.estado) params.append('estado', filtros.estado)

      const response = await api.get(`/api/admin/usuarios?${params.toString()}`)
      const usuarios = response.data.usuarios || []
      
      return usuarios.map((usuario: UsuarioAPI) => this.normalizarUsuario(usuario))
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener usuarios')
    }
  },

  /**
   * Obtener usuario por ID
   */
  async obtenerUsuario(id: string): Promise<Usuario> {
    try {
      const response = await api.get(`/api/admin/usuarios/${id}`)
      return this.normalizarUsuario(response.data)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener usuario')
    }
  },

  /**
   * Crear nuevo usuario
   */
  async crearUsuario(usuario: UsuarioCrear): Promise<Usuario> {
    try {
      if (usuario.id_institucion) {
        let rol_miembro = ''
        if (usuario.rol === 'institucion_admin') rol_miembro = 'admin'
        else if (usuario.rol === 'institucion_supervisor') rol_miembro = 'supervisor'
        else if (usuario.rol === 'institucion_jefe') rol_miembro = 'jefe'
        else if (usuario.rol === 'institucion_usuario') rol_miembro = 'usuario'
        else if (usuario.rol === 'institucion_maestro') rol_miembro = 'maestro'
        else if (usuario.rol === 'institucion_alumno') rol_miembro = 'alumno'

        if (rol_miembro) {
          const payload = {
            ...usuario,
            rol_miembro
          }
          const response = await api.post(`/api/admin/constituciones/${usuario.id_institucion}/usuarios`, payload)
          return this.normalizarUsuario(response.data.usuario)
        }
      }

      const response = await api.post('/api/admin/usuarios', usuario)
      return this.normalizarUsuario(response.data)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al crear usuario')
    }
  },

  /**
   * Actualizar usuario
   */
  async actualizarUsuario(id: string, usuario: UsuarioActualizar): Promise<Usuario> {
    try {
      const response = await api.put(`/api/admin/usuarios/${id}`, usuario)
      return this.normalizarUsuario(response.data)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al actualizar usuario')
    }
  },

  /**
   * Eliminar usuario
   */
  async eliminarUsuario(id: string): Promise<void> {
    try {
      await api.delete(`/api/admin/usuarios/${id}`)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al eliminar usuario')
    }
  },

  /**
   * Cambiar estado de usuario (activar/desactivar)
   */
  async cambiarEstadoUsuario(id: string, activo: boolean): Promise<Usuario> {
    try {
      const response = await api.patch(`/api/admin/usuarios/${id}/estado`, { activo })
      return this.normalizarUsuario(response.data)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al cambiar estado del usuario')
    }
  },

  /**
   * Obtener estadísticas de usuarios
   */
  async obtenerEstadisticas(): Promise<EstadisticasUsuarios> {
    try {
      const response = await api.get('/api/admin/usuarios/estadisticas')
      return response.data
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener estadísticas')
    }
  },

  /**
   * Resetear contraseña de usuario
   */
  async resetearPassword(id: string): Promise<{ password_temporal: string; sesiones_invalidadas?: boolean; token_version?: number }> {
    try {
      const response = await api.post(`/api/admin/usuarios/${id}/reset-password`)
      return response.data
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al resetear contraseña')
    }
  }
}
